import React from "react"
import { CircularProgress, Paper, Stack, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"

type MonthlyStat = {
    month: string
    revenueMinor: number
}

type SimpleSalesStatsProps = {
    title?: string
    revenueTotalMinor?: number
    monthly?: MonthlyStat[]
    loading?: boolean
    emptyMessage?: string
}

function SimpleSalesStats({
    title,
    revenueTotalMinor = 0,
    monthly = [],
    loading = false,
    emptyMessage,
}: SimpleSalesStatsProps) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language || "fr"
    const formatMinor = (value: number) =>
        `${(Number(value || 0) / 100).toFixed(2)} €`
    const formatMonth = (month: string) => {
        const [year, monthNumber] = month.split("-")
        const y = Number(year)
        const m = Number(monthNumber)
        if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) {
            return month
        }
        return new Date(y, m - 1, 1).toLocaleDateString(locale, {
            month: "short",
            year: "numeric",
        })
    }

    const effectiveTitle = title ?? t("simpleSalesStats.title")
    const totalLabel = t("simpleSalesStats.totalLabel")
    const effectiveEmptyMessage = emptyMessage ?? t("simpleSalesStats.empty")
    const loadingLabel = t("simpleSalesStats.loading")

    return (
        <Paper sx={{ p: 2 }}>
            <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
            >
                {effectiveTitle}
            </Typography>
            <Stack spacing={1}>
                <Typography sx={{ fontWeight: 700 }}>
                    {totalLabel}: {formatMinor(revenueTotalMinor)}
                </Typography>

                {loading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                            {loadingLabel}
                        </Typography>
                    </Stack>
                ) : monthly.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {effectiveEmptyMessage}
                    </Typography>
                ) : (
                    <Stack spacing={0.5}>
                        {monthly.map((row) => (
                            <Stack
                                key={`${row.month}-${row.revenueMinor}`}
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {formatMonth(row.month)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {formatMinor(row.revenueMinor)}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Paper>
    )
}

export default SimpleSalesStats
